import React, { useEffect } from 'react';

import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useClearRefinements, useRefinementList, useSortBy } from 'react-instantsearch';
import { useParams } from 'react-router-dom';

import { FILTERS } from '~/const';
import { useDiscoverContext } from '~/context/DiscoverContext';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import useIsSsku from '~/hooks/tenant/useIsSsku';
import useCategoriesTranslations from '~/hooks/useCategoriesTranslations';
import useRefinementSkills from '~/hooks/useRefinementSkills';
import useSERPNavigation from '~/hooks/useSERPNavigation';
import useSearchClient from '~/hooks/useSearchClient';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { CategoryNavigationBlock } from '~/pages/DiscoverPage/components/CategoryNavigationBlock/CategoryNavigationBlock';
import ExpertNavigationBlock from '~/pages/DiscoverPage/components/ExpertNavigationBlock/ExpertNavigationBlock';
import FilterWrapper from '~/pages/DiscoverPage/components/FilterWrapper/FilterWrapper';
import {
  getDurationTranslations,
  getSearchableTypesTranslations,
} from '~/pages/DiscoverPage/components/FiltersPanel/helpers';
import { HierarchicalMenu as CustomHierarchicalMenu } from '~/pages/DiscoverPage/components/HierarchicalMenu/HierarchicalMenu';
import { RefinementList as CustomRefinementList } from '~/pages/DiscoverPage/components/RefinementList/RefinementList';
import RefinementListView from '~/pages/DiscoverPage/components/RefinementList/RefinementListView';
import SkillNavigationBlock from '~/pages/DiscoverPage/components/SkillNavigationBlock/SkillNavigationBlock';
import SmallSearch from '~/pages/DiscoverPage/components/SmallSearch/SmallSearch';
import { useDifficultyTranslations } from '~/pages/DiscoverPage/hooks';
import { HITS_TYPES } from '~/pages/const';
import getSubtitlesTranslations from '~/utils/getSubtitlesTranslations';

const CATEGORY_FILTER = [FILTERS.Category, FILTERS.Subcategory];

interface FiltersPanelProps {
  isMobile: boolean;
  resetTypeFilter?: boolean;
  isSearching?: boolean;

  loading?: boolean;
  initialExpert?: string;
  initialCategory?: string;
  initialSkillId?: string;
}

const FiltersPanel: React.FC<FiltersPanelProps> = ({
  isMobile,
  resetTypeFilter = false,
  isSearching = false,
  loading = false,
  initialExpert,
  initialCategory,
  initialSkillId,
}) => {
  const { t, i18n } = useTenantTranslation();
  const params = useParams();
  const serpType = useSERPNavigation();
  const isSsku = useIsSsku();
  const { setRefineSearchType } = useDiscoverContext();
  const { userInfo } = useUserInfoContext();

  const { indexName } = useSearchClient();

  const onCategoriesPage = !!params?.category;
  const onExpertPage = !!params?.expertSlug;
  const onSkillPage = !!params?.skill;
  const onInsightsPage = serpType === HITS_TYPES.NEWS;
  const onGlossaryPage = serpType === HITS_TYPES.GLOSSARY;
  const onPathwaysPage = serpType === HITS_TYPES.PATHWAY;
  const onCoursesPage = serpType === HITS_TYPES.COURSE;

  const sortedTitleAscIndexName = `${indexName}_title_${i18n.language}_asc`;
  const sortedPublishedAtDescIndexName = `${indexName}_published_at_desc`;
  const sortedFreeForAllDescIndexName = `${indexName}_free_for_all_desc`;

  const searchableTypesTranslations = getSearchableTypesTranslations(t);
  const durationTranslations = getDurationTranslations(t);
  const subtitlesTranslations = getSubtitlesTranslations(t);

  const { loading: loadingCategories, translations: categoriesTranslations } =
    useCategoriesTranslations();
  const { loading: loadingDifficulties, translations: difficultyTranslations } =
    useDifficultyTranslations();

  const { refine } = useSortBy({
    items: [
      { value: sortedTitleAscIndexName, label: 'Sort Title Asc' },
      { value: sortedPublishedAtDescIndexName, label: 'Sort Published at Desc' },
      { value: sortedFreeForAllDescIndexName, label: 'Sort Free for all Desc' },
      { value: indexName || '', label: 'Default' },
    ],
  });

  const {
    refine: refineSkillAttribute,
    items: skills,
    translations: skillsTranslations,
  } = useRefinementSkills();

  const { refine: clearRefinements } = useClearRefinements();

  const { refine: refineSearchType, items: searchTypeItems } = useRefinementList({
    attribute: FILTERS.SearchType,
  });

  useEffect(() => {
    if (onSkillPage && !isSearching && initialSkillId) {
      clearRefinements();
      refineSkillAttribute(initialSkillId);
    }
    if (onGlossaryPage && !isSearching) {
      refine(sortedTitleAscIndexName);
    } else if (onInsightsPage && !isSearching) {
      refine(sortedPublishedAtDescIndexName);
    } else if (!userInfo) {
      refine(sortedFreeForAllDescIndexName);
    } else {
      refine(indexName);
    }
  }, [
    isSearching,
    initialSkillId,
    refine,
    refineSkillAttribute,
    clearRefinements,
    onGlossaryPage,
    sortedTitleAscIndexName,
    onInsightsPage,
    onSkillPage,
    sortedPublishedAtDescIndexName,
    sortedFreeForAllDescIndexName,
    userInfo,
    indexName,
  ]);

  useEffect(() => {
    setRefineSearchType(() => refineSearchType);
  }, [refineSearchType, setRefineSearchType]);

  if (onCoursesPage) {
    return null;
  }

  return (
    <Wrapper>
      {onGlossaryPage && (
        <SearchWrapper>
          <SmallSearch />
        </SearchWrapper>
      )}
      {onExpertPage && (
        <FilterWrapper id="experts" title={t('page.discover.experts')} withoutToggle>
          <ExpertNavigationBlock attribute={FILTERS.Expert} initialExpert={initialExpert} />
        </FilterWrapper>
      )}
      <FilterWrapper
        id="categories"
        title={t('page.discover.categories')}
        withoutToggle={onCategoriesPage}
      >
        {onCategoriesPage ? (
          <>
            <CategoryNavigationBlock
              attributes={CATEGORY_FILTER}
              loading={loading || loadingCategories}
              initialCategory={initialCategory}
            />
          </>
        ) : (
          <CustomHierarchicalMenu
            attributes={CATEGORY_FILTER}
            loading={loading || loadingCategories}
            loadingItems={8}
            translations={categoriesTranslations}
            data-testid="categoriesFilter"
          />
        )}
      </FilterWrapper>
      {!(isSsku || onInsightsPage || onGlossaryPage) && skills.length > 0 && (
        <FilterWrapper
          id="skill"
          title={t('page.discover.skills')}
          data-testid="skillFilter"
          withoutToggle={onSkillPage}
        >
          {onSkillPage ? (
            <SkillNavigationBlock />
          ) : (
            <RefinementListView
              attribute={FILTERS.Skills}
              loading={loading}
              loadingItems={5}
              items={skills}
              itemsToShowCount={skills.length}
              refine={refineSkillAttribute}
              translations={skillsTranslations}
            />
          )}
        </FilterWrapper>
      )}
      {!(onInsightsPage || onGlossaryPage) && (
        <FilterWrapper id="level" title={t('page.discover.level')} data-testid="difficultyFilter">
          <CustomRefinementList
            attribute={FILTERS.Difficulty}
            loadingItems={3}
            loading={loading || loadingDifficulties}
            translations={difficultyTranslations}
          />
        </FilterWrapper>
      )}
      {!(onExpertPage || onGlossaryPage || onPathwaysPage) && (
        <FilterWrapper id="experts" title={t('page.discover.experts')} data-testid="expertsFilter">
          <CustomRefinementList
            attribute={FILTERS.Expert}
            showMore
            searchable
            collapseLimit={5}
            showMoreLimit={50}
            delay={isMobile ? 500 : 0}
            loading={loading}
          />
        </FilterWrapper>
      )}
      {!(onInsightsPage || onGlossaryPage) && (
        <FilterWrapper
          id="duration"
          title={t('page.discover.duration')}
          data-testid="durationFilter"
        >
          <CustomRefinementList
            attribute={FILTERS.Duration}
            loadingItems={4}
            loading={loading}
            translations={durationTranslations}
          />
        </FilterWrapper>
      )}
      {!onPathwaysPage && !onInsightsPage && !onGlossaryPage && (
        <FilterWrapper
          id="subtitles"
          title={t('page.discover.subtitles')}
          data-testid="subtitlesFilter"
        >
          <CustomRefinementList
            attribute={FILTERS.Subtitles}
            loading={loading}
            translations={subtitlesTranslations}
            limit={15}
          />
        </FilterWrapper>
      )}
      {resetTypeFilter && (
        <FilterWrapper id="type" title={t('page.discover.type')} data-testid="searchTypeFilter">
          <RefinementListView
            attribute={FILTERS.SearchType}
            loadingItems={4}
            loading={loading}
            refine={refineSearchType}
            items={searchTypeItems}
            itemsToShowCount={searchTypeItems?.length}
            translations={searchableTypesTranslations}
          />
        </FilterWrapper>
      )}
    </Wrapper>
  );
};

export default FiltersPanel;

const Wrapper = styled(Box)`
  ${({ theme }) => theme.breakpoints.up('xl')} {
    width: 300px;
  }
`;
const SearchWrapper = styled('div')`
  border-bottom: 1px solid ${({ theme }) => theme.palette.common.violetAnalyticsCard};
  margin-top: 12px;
`;
